import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import lazyPanda from '../assets/animations/Lazy Panda.json';
import './Reflect.css';

const Reflect = ({ onBack, onNavigateToDashboard, onNavigateToZenPlay, onNavigateToJourney }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [reflectionData, setReflectionData] = useState({});
  const [currentLearning, setCurrentLearning] = useState('');
  const [currentGratitude, setCurrentGratitude] = useState('');
  const [currentMood, setCurrentMood] = useState(2); // Add mood tracking

  // Get the last 4 days including today
  const getLast5Days = () => {
    const days = [];
    const today = new Date();
    
    for (let i = 3; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNumber = date.getDate();
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const fullDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      days.push({
        dayName,
        dayNumber,
        monthName,
        fullDate,
        isToday: i === 0
      });
    }
    
    return days;
  };

  const [days] = useState(getLast5Days()); // This still uses the same function name for backward compatibility

  // Load reflection data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('reflectionData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setReflectionData(parsedData);
    }
  }, []);

  // Update current values when reflectionData or selectedDay changes
  useEffect(() => {
    if (selectedDay && Object.keys(reflectionData).length > 0) {
      const dayData = reflectionData[selectedDay.fullDate] || {};
      setCurrentLearning(dayData.learning || '');
      setCurrentGratitude(dayData.gratitude || '');
      setCurrentMood(dayData.mood !== undefined ? dayData.mood : 2);
    }
  }, [reflectionData, selectedDay]);

  // Save reflection data to localStorage
  const saveReflectionData = (data) => {
    localStorage.setItem('reflectionData', JSON.stringify(data));
    setReflectionData(data);
  };

  // Handle day selection
  const handleDaySelect = (day) => {
    // Save current data before switching
    if (selectedDay && (currentLearning || currentGratitude || currentMood !== 2)) {
      const updatedData = {
        ...reflectionData,
        [selectedDay.fullDate]: {
          learning: currentLearning || '',
          gratitude: currentGratitude || '',
          mood: currentMood,
          lastUpdated: new Date().toISOString()
        }
      };
      saveReflectionData(updatedData);
    }

    // Set the selected day - useEffect will handle loading the data
    setSelectedDay(day);
  };

  // Handle saving current reflection
  const handleSave = () => {
    if (selectedDay) {
      const updatedData = {
        ...reflectionData,
        [selectedDay.fullDate]: {
          learning: currentLearning || '',
          gratitude: currentGratitude || '',
          mood: currentMood,
          lastUpdated: new Date().toISOString()
        }
      };
      saveReflectionData(updatedData);
      
      // Show success feedback
      const saveBtn = document.querySelector('.save-btn');
      if (saveBtn) {
        saveBtn.textContent = 'Saved! ✓';
        setTimeout(() => {
          saveBtn.textContent = 'Save Reflection';
        }, 2000);
      }
    }
  };

  // Check if day has any content
  const hasContent = (day) => {
    const dayData = reflectionData[day.fullDate];
    return dayData && (dayData.learning || dayData.gratitude || (dayData.mood !== undefined && dayData.mood !== 2));
  };

  return (
    <div className="reflect-container">
      <div className="falling-leaves">
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
      </div>

      <div className="reflect-content">
        {/* Header */}
        <div className="reflect-header">
          <h1 className="reflect-title">Daily Reflections 📖</h1>
          <p className="reflect-subtitle">Your personal journey of growth and gratitude</p>
        </div>

        {/* Day Selection */}
        <div className="day-selection">
          <h2 className="section-title">Choose a Day to Reflect</h2>
          <div className="days-container">
            {days.map((day, index) => (
              <div
                key={day.fullDate}
                className={`day-card ${selectedDay?.fullDate === day.fullDate ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                onClick={() => handleDaySelect(day)}
              >
                <div className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={hasContent(day)}
                    readOnly
                    className="day-check"
                  />
                  <span className="checkmark">✓</span>
                </div>
                <div className="day-info">
                  <div className="day-name">{day.dayName}</div>
                  <div className="day-number">{day.dayNumber}</div>
                  <div className="day-month">{day.monthName}</div>
                </div>
                {day.isToday && <div className="today-badge">Today</div>}
                {hasContent(day) && <div className="content-indicator">📝</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Reflection Form */}
        {selectedDay && (
          <div className="reflection-form">
            <div className="form-header">
              <h2 className="form-title">
                Reflecting on {selectedDay.dayName}, {selectedDay.monthName} {selectedDay.dayNumber}
                {selectedDay.isToday && <span className="today-indicator"> (Today)</span>}
              </h2>
            </div>

            <div className="reflection-sections">
              {/* Mood Section */}
              <div className="reflection-section">
                <div className="section-header">
                  <div className="section-icon">😊</div>
                  <h3 className="section-label">How was your mood?</h3>
                </div>
                <div className="mood-selector">
                  {['😢', '😕', '😐', '🙂', '😊'].map((emoji, index) => (
                    <button
                      key={index}
                      className={`mood-option ${currentMood === index ? 'selected' : ''}`}
                      onClick={() => setCurrentMood(index)}
                    >
                      <span className="mood-emoji">{emoji}</span>
                      <span className="mood-label">
                        {['Terrible', 'Not Great', 'Okay', 'Good', 'Amazing'][index]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Learning Section */}
              <div className="reflection-section">
                <div className="section-header">
                  <div className="section-icon">🌱</div>
                  <h3 className="section-label">Today's Learning</h3>
                </div>
                <textarea
                  className="reflection-textarea learning-textarea"
                  placeholder="What did you learn today? Share your insights, discoveries, or realizations..."
                  value={currentLearning}
                  onChange={(e) => setCurrentLearning(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Gratitude Section */}
              <div className="reflection-section">
                <div className="section-header">
                  <div className="section-icon">🙏</div>
                  <h3 className="section-label">Gratitude & Appreciation</h3>
                </div>
                <textarea
                  className="reflection-textarea gratitude-textarea"
                  placeholder="What are you grateful for today? Big or small, every blessing counts..."
                  value={currentGratitude}
                  onChange={(e) => setCurrentGratitude(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="form-actions">
              <button className="save-btn" onClick={handleSave}>
                Save Reflection
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedDay && (
          <div className="empty-state">
            <div className="panda-animation-container" style={{ width: '200px', height: '200px', margin: '0 auto 10px' }}>
              <Lottie 
                animationData={lazyPanda} 
                loop={true} 
                autoplay={true}
                style={{
                  width: '100%',
                  height: '100%',
                  margin: '0 auto'
                }}
              />
            </div>
            <h2>Select a day to start reflecting</h2>
          </div>
        )}

        {/* Navigation */}
        <div className="bottom-nav">
          <div 
            className="nav-item" 
            onClick={onNavigateToDashboard || onBack}
          >
            <span className="nav-icon">🏠</span>
            <span className="nav-label">Home</span>
          </div>
          <div className="nav-item active">
            <span className="nav-icon">💭</span>
            <span className="nav-label">Reflect</span>
          </div>
          <div 
            className="nav-item" 
            onClick={onNavigateToZenPlay}
          >
            <span className="nav-icon">🎮</span>
            <span className="nav-label">Zen Play</span>
          </div>
          <div 
            className="nav-item"
            onClick={onNavigateToJourney}
          >
            <span className="nav-icon">📈</span>
            <span className="nav-label">Journey</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reflect;